import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getIdToken } from '../Firebase';

const API = `${process.env.REACT_APP_API_URL}/api/admin/app-settings`;
const TRANSFERMARKT_REFRESH_API =
  `${process.env.REACT_APP_API_URL}/api/admin/transfermarkt-refresh`;

const AppSettingsManager = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [refreshingTransfermarkt, setRefreshingTransfermarkt] = useState(false);
  const [message, setMessage] = useState('');

  const authHeaders = useCallback(async () => {
    const token = await getIdToken(false);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(API, { headers: await authHeaders() });
      setSettings(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setKey('');
    setValue('');
    setDialog('create');
  };

  const openEdit = (setting) => {
    setKey(setting.key);
    setValue(setting.value);
    setDialog('edit');
  };

  const save = async () => {
    setError('');
    try {
      const headers = await authHeaders();
      if (dialog === 'create') {
        await axios.post(API, { key, value }, { headers });
      } else {
        await axios.put(`${API}/${encodeURIComponent(key)}`, { value }, { headers });
      }
      setDialog(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const remove = async (settingKey) => {
    if (!window.confirm(`Usunąć ustawienie "${settingKey}"?`)) return;
    setError('');
    try {
      await axios.delete(`${API}/${encodeURIComponent(settingKey)}`, {
        headers: await authHeaders(),
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const refreshTransfermarkt = async () => {
    setRefreshingTransfermarkt(true);
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(
        TRANSFERMARKT_REFRESH_API,
        {},
        { headers: await authHeaders() }
      );
      setMessage(data.message || 'Import Transfermarkt został uruchomiony.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setRefreshingTransfermarkt(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Ustawienia aplikacji</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshTransfermarkt}
            disabled={refreshingTransfermarkt}
          >
            {refreshingTransfermarkt ? 'Uruchamianie...' : 'Odśwież Transfermarkt'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Dodaj</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Klucz</TableCell>
                <TableCell>Wartość</TableCell>
                <TableCell>Ostatnia zmiana</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.map((setting) => (
                <TableRow key={setting.key}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{setting.key}</TableCell>
                  <TableCell>{setting.value}</TableCell>
                  <TableCell>
                    {setting.updated_at ? new Date(setting.updated_at).toLocaleString('pl-PL') : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton aria-label={`Edytuj ${setting.key}`} onClick={() => openEdit(setting)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" aria-label={`Usuń ${setting.key}`} onClick={() => remove(setting.key)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialog != null} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{dialog === 'create' ? 'Nowe ustawienie' : 'Edytuj ustawienie'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="normal" label="Klucz" value={key}
            onChange={(event) => setKey(event.target.value)} disabled={dialog === 'edit'} />
          <TextField fullWidth margin="normal" label="Wartość" value={value}
            onChange={(event) => setValue(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Anuluj</Button>
          <Button variant="contained" onClick={save} disabled={!key.trim() || !value.trim()}>Zapisz</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppSettingsManager;
